// tree-model.js — THE tree composition module (commonization, Alex 2026-07-20:
// "the tree that is used in Onshape [should] be the leader"). Before this,
// OnshapeView had its own compose-and-namespace logic (buildTree/pruneTree)
// that Catalog/Products never touched, even though the underlying question —
// "how do you combine 0, 1, or 2 source trees into one" — is the same question
// at every call site. composeTree() is now the ONE answer; Catalog and
// Products call it in degenerate single-source form (a pure passthrough —
// same array reference back, zero behavior change, ids untouched), Onshape
// calls it in full (both sources, linked-only, namespaced).
//
// ⚠ Deliberately NOT namespacing single-source trees. Catalog/Products ids
// (`c1-N`, `c3-N|fam-M`, …) are baked into deep links, localStorage, browser
// history and hundreds of id-shape checks across both screens. Namespacing is
// only a real problem when TWO sources merge into one tree — a fresh
// `catalog_categories` id and a fresh `product_categories` id both starting
// at 1 collide (`c1-1` exists on both sides). Single-source consumption never
// has that collision, so it stays a straight passthrough. The namespace fix
// (`p:`/`c:` prefixed ids, `_srcId` to recover the original) is inherited by
// ANY future multi-source composition, not just Onshape's — that is the
// actual leadership this module provides.

// isLeafNode — a tree LEAF (as opposed to an L1/L2/L3 category branch) is a
// product node (`isProduct`, from /api/products/tree) or a catalog FAMILY node
// (`isFamily`, from /api/catalog/tree). Family-less standalone catalog lines
// are neither — only families count as catalog "linked leaves" for a
// composite/linked-only tree.
export function isLeafNode(node) {
  return !!(node.isProduct || node.isFamily);
}

// Deep-clone-prune a tree (array of root nodes): keep a leaf iff `isLinked(leaf)`
// is true; keep a branch iff at least one descendant survives pruning. This
// keeps ancestors of any kept leaf "for free" (a branch only drops once ALL its
// children are gone) and drops empty branches.
//
// NAMESPACED IDS — both source trees use the SAME id scheme (`c1-<id>`) keyed
// on DIFFERENT tables (product_categories / catalog_categories both start at
// 1), so `c1-1` can exist on both sides of a merged tree. Every kept node is
// cloned with `p:`/`c:` prefixed onto its id; `_srcId` keeps the original for
// deep links back into the source screens, `_side` says which domain a save
// routes to.
export function pruneTree(nodes, isLinked, ns) {
  const out = [];
  for (const node of nodes || []) {
    if (isLeafNode(node)) {
      if (isLinked(node)) out.push({ ...node, id: `${ns}:${node.id}`, _srcId: node.id, _side: ns });
      continue;
    }
    const children = pruneTree(node.children, isLinked, ns);
    if (children.length > 0) {
      // Drop stale rollup `counts` (draft/validated/etc.) from the clone — those
      // were computed over the FULL sub-tree, not the pruned/linked-only subset
      // shown here, so keeping them would show misleading status glyphs.
      const { counts, ...rest } = node;
      out.push({ ...rest, id: `${ns}:${node.id}`, _srcId: node.id, _side: ns, children });
    }
  }
  return out;
}

// namespaceTree — pruneTree with every leaf kept (isLinked always true). Same
// namespace/_srcId/_side treatment, no filtering. Used when composeTree merges
// two full (unfiltered) sources — not exercised by any caller today, but a
// merge with no pruning still has the identical collision, so it must not skip
// the fix.
function namespaceTree(nodes, ns) {
  return pruneTree(nodes, () => true, ns);
}

// composeTree({ catalog, products, linkedOnly, linked }) — combine 0/1/2
// source trees into one. This is the canonical shape every screen's tree is
// built from:
//   Catalog screen  → composeTree({ catalog: catalogTree })                — passthrough
//   Products screen → composeTree({ products: productTree })               — passthrough
//   Onshape screen   → composeTree({ catalog, products, linkedOnly: true, linked }) — full
//
// `linked` is the `/api/onshape/linked` payload ({ products: [{id}], families:
// [{id}] }) — required when linkedOnly is true; ignored otherwise.
export function composeTree({ catalog = null, products = null, linkedOnly = false, linked = null } = {}) {
  const hasCatalog = !!catalog;
  const hasProducts = !!products;

  if (hasCatalog && hasProducts) {
    if (linkedOnly) {
      const linkedProductIds = new Set((linked?.products || []).map(p => p.id));
      const linkedFamilyIds = new Set((linked?.families || []).map(f => f.id));
      const prunedProducts = pruneTree(products, n => linkedProductIds.has(n._productId), 'p');
      const prunedCatalog = pruneTree(catalog, n => n.isFamily && linkedFamilyIds.has(n._famId), 'c');
      // No "Products"/"Catalog" group headers — the tags + levels already
      // match the real trees, so the two hierarchies sit at the top level
      // directly (Alex).
      return [...prunedProducts, ...prunedCatalog];
    }
    return [...namespaceTree(products, 'p'), ...namespaceTree(catalog, 'c')];
  }

  // Single source — exact passthrough (same array, same ids) — zero behavior
  // change for a screen that only ever had one source to begin with.
  return hasCatalog ? catalog : (products || []);
}

// findNode / findPath — generic walks over a composeTree() result: every node
// has `.id` + `.children`, and (unlike Catalog's own line-leaf pseudo-children
// or Products' isProduct leaves) a composite tree's leaves carry no `.children`
// worth descending into, so there is no leaf-skip guard to encode here. Moved
// out of OnshapeView verbatim (same idiom Catalog/Products use internally for
// their own single-source trees, which keep their own guarded versions — see
// the module doc above for why those aren't unified here too).
export function findNode(nodes, id) {
  for (const node of nodes || []) {
    if (node.id === id) return node;
    const found = findNode(node.children, id);
    if (found) return found;
  }
  return null;
}

export function findPath(nodes, id, trail = []) {
  for (const node of nodes || []) {
    const next = [...trail, node];
    if (node.id === id) return next;
    const found = findPath(node.children, id, next);
    if (found) return found;
  }
  return null;
}

// ownOnlyTree(nodes, ownerId) — the tree as one project's OWN rows only (Alex,
// 2026-10-15: Catalog and Products start filtered to the Working-in project; "Show
// Standard" brings the rest back). A leaf (isLeafNode: product or catalog family) is
// kept iff its _ownerProjectId is that project; a branch iff something under it is.
// Ids, order and fields are untouched — unlike pruneTree there is no namespace, so
// selection and deep links still resolve against the full tree.
export function ownOnlyTree(nodes, ownerId) {
  const own = String(ownerId);
  const walk = (list) => {
    const out = [];
    for (const n of list || []) {
      if (isLeafNode(n)) { if (n._ownerProjectId != null && String(n._ownerProjectId) === own) out.push(n); continue; }
      if (n.isLine) continue;   // a family-less line has no owner of its own here
      const children = walk(n.children);
      if (children.length) out.push({ ...n, children });
    }
    return out;
  };
  return walk(nodes);
}
